import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Package, RotateCw, Store, Unplug } from "lucide-react";
import type { MerchantProOrderSyncStatus, MerchantProStoreDto } from "@finora/types";
import { ApiError } from "@finora/api-client";
import { formatMoney } from "@finora/config";
import { Button, EmptyState, Skeleton, StatusBadge } from "@finora/ui";
import { api } from "../../lib/api";

/**
 * MerchantPro integracija (02.10.2026) - ekran u web panelu, isti obrazac
 * kao ShopifyTab.tsx (vidi docs/timovi/integracije-merchantpro-povezivanje.md).
 * Za razliku od Shopify-a nema OAuth - unose se Site URL + API Key/Secret
 * direktno, i modul je poseban, samostalno licenciran (key "merchantpro").
 */

const STATUS: Record<MerchantProOrderSyncStatus, { label: string; tone: "success" | "warning" | "error" | "neutral" | "info" }> = {
  PENDING: { label: "Na čekanju", tone: "neutral" },
  FISCALIZED: { label: "Fiskalizovano", tone: "success" },
  BLOCKED_LICENSE: { label: "Čeka aktivaciju licence", tone: "warning" },
  BLOCKED_VALIDATION: { label: "Odbijeno, potrebna ispravka", tone: "error" },
  FAILED_RETRYING: { label: "Greška, pokušava ponovo", tone: "warning" },
  FAILED_FINAL: { label: "Neuspelo (iscrpljeni pokušaji)", tone: "error" },
  SKIPPED: { label: "Preskočeno", tone: "neutral" },
  REFUNDED: { label: "Stornirano", tone: "info" },
  REFUND_FAILED: { label: "Storno neuspeo", tone: "error" },
};

const RETRYABLE: MerchantProOrderSyncStatus[] = ["BLOCKED_VALIDATION", "FAILED_FINAL", "REFUND_FAILED"];

function errorText(e: unknown): string {
  if (e instanceof ApiError) return e.response.message;
  return e instanceof Error ? e.message : String(e);
}

function ConnectForm({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [siteUrl, setSiteUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");

  const connect = useMutation({
    mutationFn: () => api.createMerchantProStore({ siteUrl: siteUrl.trim(), apiKey: apiKey.trim(), apiSecret: apiSecret.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["merchantpro-stores"] });
      onClose();
    },
  });

  return (
    <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
      <h3 className="text-sm font-semibold text-slate-900">Poveži MerchantPro prodavnicu</h3>
      <p className="mt-1 text-sm text-slate-500">
        Unesi adresu prodavnice i API kredencijale iz MerchantPro admina. API Secret se posle unosa ne prikazuje,
        samo može da se zameni.
      </p>
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        <input
          value={siteUrl}
          onChange={(e) => setSiteUrl(e.target.value)}
          placeholder="https://moja-prodavnica.rs"
          className="h-10 rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
        />
        <input
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="API Key"
          className="h-10 rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
        />
        <input
          value={apiSecret}
          onChange={(e) => setApiSecret(e.target.value)}
          placeholder="API Secret"
          type="password"
          className="h-10 rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
        />
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button
          disabled={!siteUrl.trim() || !apiKey.trim() || !apiSecret.trim() || connect.isPending}
          onClick={() => connect.mutate()}
        >
          Poveži
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Otkaži
        </Button>
      </div>
      {connect.isError && <p className="mt-2 text-sm text-red-600">{errorText(connect.error)}</p>}
    </div>
  );
}

function EditCredentialsForm({ store, onClose }: { store: MerchantProStoreDto; onClose: () => void }) {
  const qc = useQueryClient();
  const [siteUrl, setSiteUrl] = useState(store.siteUrl);
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");

  const save = useMutation({
    mutationFn: () =>
      api.updateMerchantProStore(store.id, {
        ...(siteUrl.trim() !== store.siteUrl ? { siteUrl: siteUrl.trim() } : {}),
        ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
        ...(apiSecret.trim() ? { apiSecret: apiSecret.trim() } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["merchantpro-stores"] });
      onClose();
    },
  });

  return (
    <div className="mt-3 rounded-lg border border-border bg-app-bg p-3">
      <div className="grid gap-2 sm:grid-cols-3">
        <input
          value={siteUrl}
          onChange={(e) => setSiteUrl(e.target.value)}
          className="h-9 rounded-md border border-border px-2 text-sm"
        />
        <input
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="Novi API Key (ostavi prazno da ne meniš)"
          className="h-9 rounded-md border border-border px-2 text-sm"
        />
        <input
          value={apiSecret}
          onChange={(e) => setApiSecret(e.target.value)}
          placeholder="Novi API Secret (ostavi prazno da ne meniš)"
          type="password"
          className="h-9 rounded-md border border-border px-2 text-sm"
        />
      </div>
      <div className="mt-2 flex items-center gap-2">
        <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
          Sačuvaj
        </Button>
        <Button size="sm" variant="secondary" onClick={onClose}>
          Otkaži
        </Button>
      </div>
      {save.isError && <p className="mt-2 text-xs text-red-600">{errorText(save.error)}</p>}
    </div>
  );
}

function SerialNumberCell({ storeId, syncId, value }: { storeId: string; syncId: string; value: string | null }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");

  const save = useMutation({
    mutationFn: () => api.updateMerchantProOrderSerial(storeId, syncId, draft.trim() || null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["merchantpro-orders", storeId] });
      setEditing(false);
    },
  });

  if (!editing) {
    return (
      <button
        onClick={() => {
          setDraft(value ?? "");
          setEditing(true);
        }}
        className="text-left text-xs text-brand hover:underline"
      >
        {value ? value : "+ Upiši serijski broj"}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Serijski broj opreme"
        className="h-8 w-36 rounded-md border border-border px-2 text-xs"
      />
      <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
        Sačuvaj
      </Button>
      <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>
        Otkaži
      </Button>
      {save.isError && <span className="text-xs text-red-600">{errorText(save.error)}</span>}
    </div>
  );
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-slate-600">{label}</span>
        <button
          type="button"
          onClick={async () => {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          className="flex items-center gap-1 text-xs text-brand hover:underline"
        >
          <Copy className="h-3.5 w-3.5" aria-hidden /> {copied ? "Kopirano!" : "Kopiraj"}
        </button>
      </div>
      <textarea
        readOnly
        value={value}
        rows={Math.min(value.split("\n").length, 14)}
        onClick={(e) => (e.target as HTMLTextAreaElement).select()}
        className="w-full resize-none rounded-md border border-border bg-surface px-2 py-1.5 font-mono text-xs text-slate-700"
      />
    </div>
  );
}

/**
 * MerchantPro webhook "order.payment_completed" (03.10.2026, vlasnikov
 * predlog) - realtime okidač umesto čekanja na cron (do 3 min), plus kanal
 * za serijski broj / izbor štampa-mejl preko napomene porudžbine (vidi
 * MerchantProWebhookController.parseAdminComments). Token + gotovi šabloni
 * se generišu ovde da bi se samo kopirali u MerchantPro admin ekran, bez
 * ručnog sastavljanja.
 */
function WebhookPanel({ store }: { store: MerchantProStoreDto }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["merchantpro-webhook-info", store.id],
    queryFn: () => api.getMerchantProWebhookInfo(store.id),
    enabled: open,
  });
  const generate = useMutation({
    mutationFn: () => api.generateMerchantProWebhookToken(store.id),
    onSuccess: (info) => {
      qc.setQueryData(["merchantpro-webhook-info", store.id], info);
      qc.invalidateQueries({ queryKey: ["merchantpro-stores"] });
    },
  });

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="text-xs text-brand hover:underline">
        {store.webhookConfigured ? "Webhook podešen - prikaži šablone" : "Podesi webhook (realtime fiskalizacija)"}
      </button>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-app-bg p-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-900">MerchantPro webhook (realtime)</h4>
        <button onClick={() => setOpen(false)} className="text-xs text-slate-500 hover:underline">
          Sakrij
        </button>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Ubrzava fiskalizaciju (ne čeka se cron na 3 minuta) i omogućava magacioneru da PRE naplate upiše u
        napomenu porudžbine <code className="rounded bg-slate-100 px-1">s/n:12345|email</code> (pošalji fiskalni
        račun mejlom) ili <code className="rounded bg-slate-100 px-1">s/n:x|stampa</code> (odštampaj, serijski
        broj još nije poznat). Cron ostaje uključen kao rezerva ako webhook ne stigne.
      </p>

      {isLoading ? (
        <Skeleton className="mt-3 h-24 w-full" />
      ) : data?.configured ? (
        <div className="mt-3 space-y-3">
          <CopyField label="URL (MerchantPro polje &quot;URL&quot;)" value={data.url} />
          <CopyField label="Nosivost (MerchantPro polje &quot;Nosivost&quot;)" value={data.payload} />
          <CopyField label="Zaglavlja (MerchantPro polje &quot;Zaglavlja&quot;)" value={data.headers} />
          <p className="text-xs text-slate-500">
            Događaj u MerchantPro podesi na <strong>order.payment_completed</strong>.
          </p>
          <Button
            size="sm"
            variant="secondary"
            disabled={generate.isPending}
            onClick={() => {
              if (
                window.confirm(
                  "Generisati NOVI token? Stari webhook u MerchantPro-u prestaje da radi dok ne zalepiš nova Zaglavlja sa novim tokenom.",
                )
              ) {
                generate.mutate();
              }
            }}
          >
            Generiši novi token
          </Button>
        </div>
      ) : (
        <div className="mt-3">
          <Button size="sm" disabled={generate.isPending} onClick={() => generate.mutate()}>
            Generiši token i šablone
          </Button>
        </div>
      )}
      {generate.isError && <p className="mt-2 text-xs text-red-600">{errorText(generate.error)}</p>}
    </div>
  );
}

function OrdersPanel({ store }: { store: MerchantProStoreDto }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["merchantpro-orders", store.id, status],
    queryFn: () => api.listMerchantProOrders(store.id, { status: status || undefined, limit: 100 }),
    refetchInterval: 30_000,
  });
  const retry = useMutation({
    mutationFn: (syncId: string) => api.retryMerchantProOrder(store.id, syncId),
    onSettled: () => qc.invalidateQueries({ queryKey: ["merchantpro-orders", store.id] }),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h4 className="text-sm font-semibold text-slate-900">Porudžbine</h4>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-8 rounded-md border border-border px-2 text-sm">
          <option value="">Sve</option>
          {(Object.keys(STATUS) as MerchantProOrderSyncStatus[]).map((s) => (
            <option key={s} value={s}>
              {STATUS[s].label}
            </option>
          ))}
        </select>
      </div>
      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : !data || data.items.length === 0 ? (
        <p className="text-sm text-slate-500">Nema porudžbina.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Porudžbina</th>
              <th>Iznos</th>
              <th>Status</th>
              <th>Serijski broj</th>
              <th>Poruka / dokument</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.items.map((o) => (
              <tr key={o.id}>
                <td>
                  <div className="text-slate-800">{o.orderNumber ?? o.merchantProOrderId}</div>
                  <div className="text-xs text-slate-400">{new Date(o.createdAt).toLocaleString("sr-RS")}</div>
                </td>
                <td className="text-slate-700">{o.totalAmount ? formatMoney(o.totalAmount) : "—"}</td>
                <td>
                  <StatusBadge label={STATUS[o.status]?.label ?? o.status} tone={STATUS[o.status]?.tone ?? "neutral"} />
                  {o.writebackStatus === "FAILED" && (
                    <div className="mt-1 text-xs text-amber-600">Upis u MerchantPro nije uspeo (ponavlja se)</div>
                  )}
                </td>
                <td>
                  <SerialNumberCell storeId={store.id} syncId={o.id} value={o.equipmentSerialNumber} />
                </td>
                <td className="max-w-md text-xs text-slate-500" title={o.lastError ?? undefined}>
                  {o.lastError ?? ""}
                  {o.fiscalDocumentId && (
                    <div className="mt-1 font-mono text-slate-600">Dokument: {o.fiscalDocumentId}</div>
                  )}
                </td>
                <td>
                  {RETRYABLE.includes(o.status) && (
                    <Button variant="secondary" disabled={retry.isPending} onClick={() => retry.mutate(o.id)}>
                      <RotateCw className="mr-1 h-4 w-4" aria-hidden /> Pokušaj ponovo
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * Faza 3 (artikli/cene) + Faza 4 (zalihe) - ekran "Artikli" (04.10.2026,
 * vlasnikov zahtev: "mnogo pomaze korisniku moze da vodi magacin
 * kompletno"). "Uvezi artikle" povezuje postojeće artikle po šifri (SKU) i
 * pravi nove koji ne postoje; nakon toga cena/naziv se periodično (na 30
 * min) osvežava sa MerchantPro strane, a zaliha ide u SUPROTNOM smeru -
 * kad se u Finori primi/izda roba, novo stanje se gura na MerchantPro
 * (vidi merchantpro-stock-push.service.ts).
 *
 * Otvaranje/zatvaranje panela je podignuto u StoreCard (dugme "Artikli i
 * zalihe" u istom redu kao "Testiraj konekciju" i ostala dugmad - 04.10.2026,
 * vlasnikov zahtev da bude pregledno/konzistentno, ne skriveni tekstualni
 * link).
 */
function ProductsPanel({ store, onClose }: { store: MerchantProStoreDto; onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["merchantpro-products", store.id],
    queryFn: () => api.listMerchantProProducts(store.id),
  });
  const importProducts = useMutation({
    mutationFn: () => api.importMerchantProProducts(store.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["merchantpro-products", store.id] }),
  });
  const toggleSync = useMutation({
    mutationFn: ({ mappingId, field, value }: { mappingId: string; field: "syncStock" | "syncPrice"; value: boolean }) =>
      api.updateMerchantProProductMapping(mappingId, { [field]: value }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["merchantpro-products", store.id] }),
  });

  return (
    <div className="rounded-lg border border-border bg-app-bg p-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-900">Artikli i zalihe</h4>
        <button onClick={onClose} className="text-xs text-slate-500 hover:underline">
          Sakrij
        </button>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        "Uvezi artikle" povezuje artikle sa sajta sa HAFiskal magacinom (po šifri - ako artikal već postoji u
        HAFiskalu, samo se poveže, ne pravi se duplikat). Posle toga cena/naziv se sami osvežavaju sa sajta na 30
        minuta, a zaliha ide obrnuto - kad primiš ili izdaš robu u HAFiskalu, novo stanje se šalje na sajt.
      </p>
      <div className="mt-3">
        <Button size="sm" disabled={importProducts.isPending} onClick={() => importProducts.mutate()}>
          <Package className="mr-1 h-4 w-4" aria-hidden /> Uvezi artikle sa sajta
        </Button>
      </div>
      {importProducts.isSuccess && (
        <p className="mt-2 text-xs text-emerald-700">
          Novi: {importProducts.data.created}, povezano sa postojećim: {importProducts.data.linked}, već povezano:{" "}
          {importProducts.data.alreadyMapped}
          {importProducts.data.skippedNoTaxRate > 0 && `, preskočeno (nema poreske stope): ${importProducts.data.skippedNoTaxRate}`}
        </p>
      )}
      {importProducts.isError && <p className="mt-2 text-xs text-red-600">{errorText(importProducts.error)}</p>}

      <div className="mt-4">
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : !data || data.length === 0 ? (
          <p className="text-sm text-slate-500">Nema povezanih artikala - klikni "Uvezi artikle sa sajta".</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Šifra</th>
                <th>Naziv</th>
                <th>Cena</th>
                <th>Zaliha → sajt</th>
                <th>Cena/naziv ← sajt</th>
                <th>Greška</th>
              </tr>
            </thead>
            <tbody>
              {data.map((m) => (
                <tr key={m.id}>
                  <td className="font-mono text-xs text-slate-600">{m.productSku}</td>
                  <td className="text-slate-800">{m.productName}</td>
                  <td className="text-slate-700">{formatMoney(m.unitPriceGross)}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={m.syncStock}
                      onChange={(e) => toggleSync.mutate({ mappingId: m.id, field: "syncStock", value: e.target.checked })}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={m.syncPrice}
                      onChange={(e) => toggleSync.mutate({ mappingId: m.id, field: "syncPrice", value: e.target.checked })}
                    />
                  </td>
                  <td className="max-w-xs text-xs text-red-600" title={m.lastPushError ?? undefined}>
                    {m.lastPushError ?? ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StoreCard({ store }: { store: MerchantProStoreDto }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["merchantpro-stores"] });

  const testConnection = useMutation({
    mutationFn: () => api.testMerchantProConnection(store.id),
    onSuccess: invalidate,
  });
  const toggleActive = useMutation({
    mutationFn: () => (store.active ? api.disableMerchantProStore(store.id) : api.enableMerchantProStore(store.id)),
    onSuccess: invalidate,
  });
  const syncNow = useMutation({
    mutationFn: () => api.syncMerchantProStoreNow(store.id),
    onSuccess: invalidate,
  });
  const disconnect = useMutation({
    mutationFn: () => api.disconnectMerchantProStore(store.id),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-5 rounded-xl border border-border bg-surface p-5 shadow-subtle">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-900">{store.siteUrl}</h3>
          <div className="font-mono text-xs text-slate-500">Ključ: {store.apiKeyMasked}</div>
          <div className="mt-2 flex flex-wrap gap-2">
            <StatusBadge label={store.active ? "Sinhronizacija uključena" : "Sinhronizacija isključena"} tone={store.active ? "success" : "neutral"} />
            <StatusBadge label={store.licenseActive ? "Licenca aktivna" : "MerchantPro integracija nije u paketu"} tone={store.licenseActive ? "success" : "warning"} />
            <StatusBadge
              label={store.lastSyncAt ? `Poslednja sinhronizacija: ${new Date(store.lastSyncAt).toLocaleString("sr-RS")}` : "Još nije sinhronizovano"}
              tone={store.lastSyncAt ? "info" : "neutral"}
            />
          </div>
          {store.lastError && <p className="mt-2 text-xs text-red-600">{store.lastError}</p>}
          {!store.licenseActive && store.licenseMessage && <p className="mt-2 text-xs text-amber-700">{store.licenseMessage}</p>}
          {!editing ? (
            <button onClick={() => setEditing(true)} className="mt-2 text-xs text-brand hover:underline">
              Izmeni Site URL / API Key / Secret
            </button>
          ) : (
            <EditCredentialsForm store={store} onClose={() => setEditing(false)} />
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" disabled={testConnection.isPending} onClick={() => testConnection.mutate()}>
            Testiraj konekciju
          </Button>
          <Button variant={productsOpen ? "primary" : "secondary"} onClick={() => setProductsOpen((o) => !o)}>
            <Package className="mr-1 h-4 w-4" aria-hidden /> Artikli i zalihe
          </Button>
          <Button variant="secondary" disabled={toggleActive.isPending} onClick={() => toggleActive.mutate()}>
            {store.active ? "Isključi sinhronizaciju" : "Uključi sinhronizaciju"}
          </Button>
          <Button variant="secondary" disabled={syncNow.isPending} onClick={() => syncNow.mutate()}>
            <RotateCw className="mr-1 h-4 w-4" aria-hidden /> Pokreni sinhronizaciju sada
          </Button>
          <Button
            variant="secondary"
            disabled={disconnect.isPending}
            onClick={() => {
              if (window.confirm("Prekinuti vezu sa ovom MerchantPro prodavnicom?")) disconnect.mutate();
            }}
          >
            <Unplug className="mr-1 h-4 w-4" aria-hidden /> Prekini vezu
          </Button>
        </div>
      </div>
      {testConnection.isSuccess && !testConnection.data.ok && (
        <p className="text-sm text-red-600">{testConnection.data.error ?? "Konekcija nije uspela."}</p>
      )}
      {testConnection.isSuccess && testConnection.data.ok && <p className="text-sm text-emerald-700">Konekcija uspešna.</p>}
      <WebhookPanel store={store} />
      {productsOpen && <ProductsPanel store={store} onClose={() => setProductsOpen(false)} />}
      <OrdersPanel store={store} />
    </div>
  );
}

export function MerchantProTab() {
  const [connecting, setConnecting] = useState(false);
  const { data: stores, isLoading } = useQuery({
    queryKey: ["merchantpro-stores"],
    queryFn: () => api.listMerchantProStores(),
  });

  return (
    <div className="space-y-4">
      {connecting || (stores && stores.length === 0) ? (
        <ConnectForm onClose={() => setConnecting(false)} />
      ) : (
        <div className="flex justify-end">
          <Button onClick={() => setConnecting(true)}>Poveži MerchantPro prodavnicu</Button>
        </div>
      )}

      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : !stores || stores.length === 0 ? (
        <EmptyState icon={Store} title="Nema povezanih prodavnica" description="Poveži MerchantPro prodavnicu iznad." />
      ) : (
        stores.map((s) => <StoreCard key={s.id} store={s} />)
      )}
    </div>
  );
}
